import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import AppModal from '../../../../../generic/ui/Modal/AppModal'
import ConfirmDialog from '../../../../../generic/ui/ConfirmDialog/ConfirmDialog'
import { GridTable, TableToolbar } from '../../../../../generic/grid-table'
import { GridColumn } from '../../../../../generic/grid-table/types/grid.types'
import GrowerForm from './components/GrowerForm'
import useTableData from '../../../../../generic/universal-table/hooks/useTableData'
import useTableEditing from '../../../../../generic/universal-table/hooks/useTableEditing'
import useTableFilters from '../../../../../generic/universal-table/hooks/useTableFilters'
import { Defaults } from '../../../../../modules/API/Defaults/defaults'
import { IClientItem } from '../../../../../modules/API/Defaults/models/clients'
import { growerTableService } from './services/growerTable.service'
import { growerTableColumns } from './configs/growerTable.columns'
import { growerTableFormFields } from './configs/growerTable.form'
import { growerTableAdapter } from './adapters/growerTable.adapter'
import { GrowerCreatePayload, GrowerUiRow, GrowerUpdatePayload } from './types/growerTable.types'
import { ColumnDefinition } from '../../../../../generic/universal-table/types/column.types'
import { FormFieldDefinition } from '../../../../../generic/universal-table/types/form.types'
import useExportToExcel from '../../../../../generic/hooks/useExportToExcel'
import columnsToExcel from '../../../../../generic/universal-table/utils/columnsToExcel'

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  width: 100%;
  height: 100%;
  padding: 0.5em;
  overflow: hidden;
`

const ErrorMessage = styled.p`
  color: tomato;
  direction: rtl;
  padding: 0.5em 1em;
`

const GrowerTableModule = () => {
  const {
    rows,
    isLoading,
    error,
    createRow,
    updateRow,
    removeRow,
  } = useTableData<GrowerUiRow, GrowerCreatePayload, GrowerUpdatePayload>(
    growerTableService,
    growerTableAdapter.getRowId
  )

  const {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTableEditing<GrowerUiRow>()

  const { searchQuery, filteredRows, handleSearch } = useTableFilters<GrowerUiRow>(rows)

  const [clients, setClients] = useState<IClientItem[]>([])

  useEffect(() => {
    Defaults.getClientsGo().then((res: IClientItem[]) => {
      setClients(res)
    })
  }, [])

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.ClientID, label: c.ClientName })),
    [clients]
  )

  const enrichedColumns = useMemo<ColumnDefinition<GrowerUiRow>[]>(
    () =>
      growerTableColumns.map((col) => {
        if (col.key === 'ClientID') {
          return {
            ...col,
            labelResolver: (id: unknown) =>
              clientOptions.find((o) => o.value === id)?.label ?? String(id),
          }
        }
        return col
      }),
    [clientOptions]
  )

  const enrichedFormFields = useMemo<FormFieldDefinition<GrowerUiRow>[]>(
    () =>
      growerTableFormFields.map((field) => {
        if (field.name === 'ClientID') {
          return { ...field, getOptions: () => clientOptions }
        }
        return field
      }),
    [clientOptions]
  )

  // Grid view reuses the enriched columns and layers on richer renderers.
  const gridColumns = useMemo<GridColumn<GrowerUiRow>[]>(
    () =>
      enrichedColumns.map((col): GridColumn<GrowerUiRow> => {
        if (col.key === 'GrowerID') return { ...col, cellRendererType: 'num' }
        return col
      }),
    [enrichedColumns]
  )

  const excelColumns = useMemo(() => columnsToExcel(enrichedColumns), [enrichedColumns])
  const { handleExport } = useExportToExcel({ columns: excelColumns, fileName: 'growers', rtl: true })

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleFormSubmit = useCallback(
    async (values: Partial<GrowerUiRow>): Promise<void> => {
      setIsSaving(true)
      try {
        if (editingMode === 'edit' && selectedRow) {
          const id = growerTableAdapter.getRowId(selectedRow)
          const payload = growerTableAdapter.toUpdatePayload({
            ...selectedRow,
            ...values,
          } as GrowerUiRow)
          await updateRow(id, payload)
        } else {
          const payload = growerTableAdapter.toCreatePayload(values as GrowerUiRow)
          await createRow(payload)
        }
        handleCloseForm()
      } finally {
        setIsSaving(false)
      }
    },
    [editingMode, selectedRow, createRow, updateRow, handleCloseForm]
  )

  const formTitle = editingMode === 'edit' ? 'עריכת גדל' : 'הוספת גדל חדש'

  return (
    <TableSection>
      {isFormOpen && (
        <AppModal
          title={formTitle}
          controlFunc={handleCloseForm}
          Children={GrowerForm}
          childrenProps={{
            fields: enrichedFormFields,
            initialValues: selectedRow ?? undefined,
            onSubmit: handleFormSubmit,
            onDelete: editingMode === 'edit' ? handleRequestDelete : undefined,
            isLoading: isSaving,
            isDeleting,
            submitLabel: editingMode === 'edit' ? 'עדכן' : 'הוסף',
          }}
          size="md"
        />
      )}

      <ConfirmDialog
        open={isConfirmDeleteOpen}
        title="מחיקת גדל"
        message="האם למחוק את הגדל? פעולה זו אינה הפיכה."
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={() =>
          handleConfirmDelete((row) => removeRow(growerTableAdapter.getRowId(row)))
        }
      />

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <GridTable<GrowerUiRow>
        title="גדלים"
        count={filteredRows.length}
        columns={gridColumns}
        rows={filteredRows}
        getRowId={growerTableAdapter.getRowId}
        isLoading={isLoading}
        onRowClick={handleOpenEdit}
        toolbarActions={
          <TableToolbar
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder="חיפוש גדל…"
            onExport={() => handleExport(filteredRows)}
            onAdd={handleOpenCreate}
            addLabel="הוסף גדל"
          />
        }
      />
    </TableSection>
  )
}

export default GrowerTableModule
